// ============================================================================
// CLEO'S SECRET — StoriesPage
// File: src/pages/StoriesPage.jsx
//
// Editorial rewrite following the landing-page section template + Stage 2
// formatting rules. Four-section structure:
//   1. Hero (cream / SAND)         — page opener with eyebrow + H2 + intro
//   2. Stories (dark / OBS)        — four editorial vignettes, each with
//                                    stacked numeral / gold rule / concern
//                                    label, large italic testimony,
//                                    attribution, and gold-bar outcome panel
//   3. CTA (cream / SAND)          — call to begin the conversation
//   4. Founding principle (dark)   — closing canonical Ankh + premise + label
//
// No em dashes in user-facing content. Body uses T.bodyLg justified with
// hyphens. Labels and eyebrows use Cinzel at weights per Stage 2 rules.
// ============================================================================

import React from 'react';
import {
  FF, P, T,
  SECTION_PADDING, CONTAINER_MAX, CONTAINER_READING,
  Eyebrow, SectionDivider, SectionHeadline,
} from '../lib/design-tokens';
import InvitationBlock from '../components/marketing/InvitationBlock';

// ───────── Story content (em-dash free) ─────────
const STORIES = [
  {
    numeral: 'I',
    name: 'Sarah B.', age: 42, location: 'South Yarra',
    concern: 'Chronic fatigue and brain fog',
    quote: "For three years, every GP told me my bloods were normal. I knew they weren't looking deeply enough. Within my first consultation at Cleo's Secret, they identified suboptimal iron, vitamin D, and a thyroid pattern that had been missed entirely.",
    outcome: "After twelve weeks on a targeted protocol, Sarah reported a complete shift in energy, clarity of thought, and sleep quality. Her Cleo Score improved from 34 to 78.",
    duration: 'Twelve-week program',
  },
  {
    numeral: 'II',
    name: 'Michelle R.', age: 47, location: 'Toorak',
    concern: 'Perimenopause and anxiety',
    quote: "I was waking at 3am every night drenched in sweat, my mood was all over the place, and I felt like I was losing myself. My previous GP said it was 'just stress.' Cleo's Secret took one look at my hormone panel and said: 'This is textbook perimenopause, and there is a great deal we can do.'",
    outcome: "Michelle's tailored program addressed her hormonal shifts alongside targeted nutritional support and stress management. Within eight weeks, night sweats had reduced by 80% and sleep quality had dramatically improved.",
    duration: 'Twelve-week program · ongoing membership',
  },
  {
    numeral: 'III',
    name: 'Jessica L.', age: 38, location: 'Brighton',
    concern: 'Weight resistance and hormonal imbalance',
    quote: "I was exercising five times a week, eating well, and gaining weight. It made no sense. The comprehensive blood panel revealed insulin resistance and cortisol patterns that explained everything. For the first time, someone connected the dots.",
    outcome: "With a protocol targeting her specific metabolic pattern, Jessica saw measurable improvements in her insulin sensitivity within six weeks. The weight began to shift, not through more exercise but through understanding.",
    duration: 'Twelve-week program',
  },
  {
    numeral: 'IV',
    name: 'Karen T.', age: 51, location: 'Malvern',
    concern: 'Post-menopausal bone health',
    quote: "I felt like I had aged ten years in two. My joints ached, my energy was gone, and I was genuinely worried about osteoporosis. My mother had it badly. The team at Cleo's Secret did not just test my hormones; they looked at everything. Bone density, inflammatory markers, nutrient levels. The report was the most thorough medical document I have ever received.",
    outcome: "Karen's personalised protocol included targeted supplementation, a movement program designed for bone health, and ongoing monitoring. After six months her inflammatory markers had normalised and her energy was 'back to my forties.'",
    duration: 'Ongoing membership',
  },
];

// ───────── StoryCard — editorial vignette ─────────
function StoryCard({ numeral, concern, quote, name, age, location, outcome, duration }) {
  return (
    <article style={{
      maxWidth: 880,
      margin: '0 auto',
      padding: 'clamp(48px, 5vw, 80px) clamp(20px, 3vw, 48px)',
    }}>
      {/* Header — stacked numeral, rule, concern label */}
      <div style={{ textAlign: 'center', marginBottom: 'clamp(36px, 4vw, 56px)' }}>
        <div style={{ ...T.numeral, color: P.GO2, marginBottom: 16 }}>{numeral}</div>
        <div style={{ width: 40, height: 1, background: P.GO, opacity: 0.55, margin: '0 auto 18px' }} />
        <div style={{ ...T.eyebrowSm, color: P.GO2 }}>{concern}</div>
      </div>

      {/* Quote — large italic testimony */}
      <blockquote style={{
        ...T.premise,
        color: 'rgba(240,232,212,0.94)',
        textAlign: 'center',
        margin: '0 auto clamp(28px, 3vw, 40px)',
        maxWidth: 740,
      }}>
        &ldquo;{quote}&rdquo;
      </blockquote>

      {/* Attribution — eyebrow-sm on dark, GO2 alpha per spec Section 5 */}
      <div style={{
        ...T.eyebrowSm,
        textAlign: 'center',
        color: 'rgba(224, 198, 133, 0.88)',
        letterSpacing: '0.3em',
        marginBottom: 'clamp(40px, 4.5vw, 56px)',
      }}>
        {name} &middot; Age {age} &middot; {location}
      </div>

      {/* Outcome panel — gold left bar */}
      <div style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'clamp(28px, 3vw, 40px) clamp(24px, 2.8vw, 36px)',
        background: 'rgba(201,169,97,0.06)',
        borderLeft: '2px solid ' + P.GO,
      }}>
        <div style={{
          ...T.eyebrowSm,
          color: P.GO2,
          marginBottom: 14,
        }}>Outcome</div>
        <p style={{
          ...T.bodyLg,
          color: 'rgba(240,232,212,0.86)',
          textAlign: 'justify',
          hyphens: 'auto',
        }}>{outcome}</p>
        <div style={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 'clamp(11px, 0.9vw, 13px)',
          fontWeight: 700,
          color: 'rgba(210, 184, 134, 0.85)',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          marginTop: 20,
        }}>{duration}</div>
      </div>
    </article>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// StoriesPage
// ════════════════════════════════════════════════════════════════════════════
export default function StoriesPage({ onNavigate }) {
  var goIntake = function() { if (onNavigate) onNavigate('intake'); };

  return (
    <div style={{ background: P.PAP, color: P.INK, fontFamily: FF }}>

      {/* ═════════ SECTION 1 — Hero (cream, page opener) ═════════ */}
      <section style={{ background: P.SAND, padding: SECTION_PADDING }}>
        <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto', textAlign: 'center' }}>
          <Eyebrow center>Patient stories</Eyebrow>
          <div style={{ marginTop: 'clamp(24px, 2.5vw, 36px)' }}>
            <SectionHeadline
              roman="Real women."
              italic="Real answers."
            />
          </div>
          <div style={{ maxWidth: 740, margin: '0 auto' }}>
            <p style={{
              ...T.bodyLg,
              color: P.TX,
              textAlign: 'justify',
              hyphens: 'auto',
            }}>
              Every woman who walks through our doors has a story of being dismissed, undertested, or told that everything looks normal. These are their stories of what happened when someone finally listened, ordered the right tests, and named the pattern.
            </p>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 2 — Stories (dark) ═════════ */}
      <section style={{ background: P.OBS, padding: SECTION_PADDING, color: P.IVY }}>
        <div style={{ maxWidth: CONTAINER_MAX, margin: '0 auto' }}>
          <SectionDivider onDark />
          <div style={{ marginTop: 48, marginBottom: 28, textAlign: 'center' }}>
            <Eyebrow center color={P.GO2}>Four journeys</Eyebrow>
          </div>
          <SectionHeadline
            roman="Different patterns."
            italic="Same posture."
            onDark
          />
          <div style={{ maxWidth: 820, margin: '0 auto clamp(56px, 5.5vw, 80px)' }}>
            <p style={{
              ...T.bodyLg,
              color: 'rgba(240,232,212,0.84)',
              textAlign: 'justify',
              hyphens: 'auto',
            }}>
              Four women, four clinical pictures, one shared experience. Each came to us after years of having her concerns minimised. Each left with something she had not encountered before: an actual diagnostic picture, named and explained, and a treatment program built around her.
            </p>
          </div>

          {/* Story cards with dividers between */}
          <div>
            {STORIES.map(function(story, i) {
              return (
                <React.Fragment key={story.numeral}>
                  <StoryCard {...story} />
                  {i < STORIES.length - 1 && (
                    <div style={{
                      maxWidth: 880,
                      margin: '0 auto',
                      padding: 'clamp(32px, 3.5vw, 56px) 0',
                    }}>
                      <SectionDivider onDark />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 3 — Invitation (cream / SAND) ═════════
          Refactored from inline CTA to InvitationBlock atom for consistency
          with LandingPage + MembershipPage. Custom eyebrow + title + subtitle
          overrides preserve the page-specific editorial register. */}
      <section style={{ background: P.SAND, padding: SECTION_PADDING }}>
        <InvitationBlock
          eyebrowText="YOUR TURN"
          titleRoman="Your story starts with"
          titleItalic="a conversation."
          subtitle={
            <React.Fragment>
              <span style={{ display: 'block' }}>
                The women in these stories began the same way you might today: by speaking with our intake specialist Cleo.
              </span>
              <span style={{ display: 'block', marginTop: 6 }}>
                It costs you nothing to find out what a thorough clinical picture would look like.
              </span>
            </React.Fragment>
          }
          onCtaClick={goIntake}
        />
      </section>

      {/* Founding-principle close retired per doc — InvitationBlock above is
          now the closing element. */}
    </div>
  );
}
